import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Globe, Smartphone, PlayCircle, Presentation, Figma, Github, FileCode2, Package,
  Video, Linkedin, Instagram, Facebook, Twitter, Youtube, FileText, ExternalLink, Plus, Trash2, Pencil, Check,
  X, FlaskConical, Users, Mail, Copy, Clock, Rocket, TrendingUp, Trophy, Link2, Eye,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import FileUpload from '@/components/FileUpload';
import { UserSearchPicker } from '@/components/venture/UserSearchPicker';
import { TeamMemberFinder } from '@/components/venture/TeamMemberFinder';
import { ProductImages } from '@/components/venture/ProductImages';
import { useApi } from '@/hooks/use-api';

export const LINKS_META = [
  { key: 'website', label: 'Website', icon: Globe, ph: 'https://yourventure.com', kind: 'url' },
  { key: 'app_android', label: 'Android app', icon: Smartphone, ph: 'https://play.google.com/store/apps/details?id=…', kind: 'url', badge: '/badges/android-icon.svg' },
  { key: 'app_ios', label: 'iOS app', icon: Smartphone, ph: 'https://apps.apple.com/…', kind: 'url', badge: '/badges/ios-icon.svg' },
  { key: 'demo', label: 'Demo', icon: PlayCircle, ph: 'Live demo link', kind: 'url_or_file', accept: 'video/mp4,video/webm,video/quicktime' },
  { key: 'deck', label: 'Pitch Deck', icon: Presentation, ph: 'Deck link', kind: 'url_or_file', accept: '.pdf,application/pdf' },
  { key: 'figma', label: 'Figma', icon: Figma, ph: 'Figma file', kind: 'url' },
  { key: 'github', label: 'GitHub', icon: Github, ph: 'Repo / org', kind: 'url' },
  { key: 'api_docs', label: 'API Docs', icon: FileCode2, ph: 'Docs link', kind: 'url_or_file', accept: '.pdf,.md,.txt,application/pdf' },
  { key: 'media_kit', label: 'Media Kit', icon: Package, ph: 'Brand assets', kind: 'url_or_file', accept: '.zip,.pdf,application/zip,application/pdf' },
  { key: 'product_video', label: 'Product Video', icon: Video, ph: 'YouTube / Loom / upload', kind: 'url_or_file', accept: 'video/mp4,video/webm,video/quicktime' },
  { key: 'linkedin', label: 'LinkedIn', icon: Linkedin, ph: 'Company page', kind: 'url' },
  { key: 'instagram', label: 'Instagram', icon: Instagram, ph: '@handle link', kind: 'url' },
  { key: 'facebook', label: 'Facebook', icon: Facebook, ph: 'facebook.com/yourpage', kind: 'url' },
  { key: 'twitter', label: 'X / Twitter', icon: Twitter, ph: 'x.com/yourhandle', kind: 'url' },
  { key: 'youtube', label: 'YouTube', icon: Youtube, ph: 'youtube.com/@channel', kind: 'url' },
  { key: 'docs', label: 'Docs', icon: FileText, ph: 'Notion / GDrive', kind: 'url_or_file', accept: '.pdf,.doc,.docx' },
];

const PRODUCT_KEYS = ['website', 'app_android', 'app_ios', 'demo', 'deck', 'figma', 'github', 'api_docs', 'media_kit', 'product_video'];

export const timeAgo = (iso) => {
  if (!iso) return '';
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 7) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
};

/* ───────────────────────── Product ───────────────────────── */

const STATUS_META = {
  live: { label: '✓ Live', cls: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700' },
  broken: { label: 'Broken', cls: 'border-rose-500/40 bg-rose-500/10 text-rose-700' },
  private: { label: 'Private', cls: 'border-amber-500/40 bg-amber-500/10 text-amber-700' },
};

export const ProductTab = ({ venture, onPatch, openSettings }) => {
  const api = useApi();
  const links = venture.links || {};
  const shots = venture.screenshots || [];
  const [statuses, setStatuses] = useState(null);
  useEffect(() => {
    api.get('/api/venture/me/links/status').then((d) => setStatuses(d.links || {})).catch(() => setStatuses({}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venture.links]);

  return (
    <div className="space-y-8" data-testid="venture-product-tab">
      <div>
        <p className="font-grotesk text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Product surfaces</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {PRODUCT_KEYS.map((k) => {
            const meta = LINKS_META.find((l) => l.key === k);
            const val = links[k];
            const st = statuses?.[k];
            const badge = st && STATUS_META[st.status];
            return val ? (
              <a key={k} href={val} target="_blank" rel="noreferrer"
                className="group rounded-xl border border-border bg-card p-4 hover:border-foreground/40 transition-colors"
                data-testid={`product-link-${k}`}>
                <div className="flex items-center justify-between">
                  <meta.icon className="h-5 w-5" />
                  {badge ? (
                    <Badge variant="outline" className={`text-[10px] ${badge.cls}`} data-testid={`product-status-${k}`}>{badge.label}</Badge>
                  ) : (
                    <ExternalLink className="h-3.5 w-3.5 text-muted-foreground group-hover:text-foreground" />
                  )}
                </div>
                <p className="font-medium mt-3">{meta.label}</p>
                <p className="text-[11px] text-muted-foreground truncate mt-0.5">{val.replace(/^https?:\/\//, '')}</p>
                {st?.updated_at && <p className="text-[10px] text-muted-foreground/70 mt-1">Updated {timeAgo(st.updated_at)}</p>}
              </a>
            ) : (
              <button key={k} type="button" onClick={() => openSettings('links')}
                className="rounded-xl border border-dashed border-border p-4 text-left hover:border-foreground/30 hover:bg-secondary/40 transition-colors"
                data-testid={`product-link-add-${k}`}>
                <div className="flex items-center justify-between">
                  <meta.icon className="h-5 w-5 text-muted-foreground/50" />
                  <Badge variant="outline" className="text-[10px] border-dashed text-muted-foreground/70">Missing</Badge>
                </div>
                <p className="font-medium mt-3 text-muted-foreground">{meta.label}</p>
                <p className="text-[11px] text-muted-foreground/60 mt-0.5 flex items-center gap-1"><Plus className="h-3 w-3" /> Add link</p>
              </button>
            );
          })}
        </div>
      </div>

      <ProductImages value={shots} onChange={(next) => onPatch({ screenshots: next })} testidPrefix="product-images" />
    </div>
  );
};

/* ───────────────────────── Growth ───────────────────────── */

const METRICS_META = [
  { key: 'customers', label: 'Customers' },
  { key: 'revenue', label: 'Revenue · ₹' },
  { key: 'mrr', label: 'MRR · ₹' },
  { key: 'users', label: 'Users' },
  { key: 'interviews', label: 'Interviews' },
  { key: 'waitlist', label: 'Waitlist' },
  { key: 'retention', label: 'Retention %' },
];

export const GrowthTab = ({ venture, onPatch }) => {
  const [editing, setEditing] = useState(null);
  const [val, setVal] = useState('');
  const [newExp, setNewExp] = useState('');
  const m = venture.metrics || {};
  const exps = venture.experiments || [];

  const saveMetric = async () => {
    const n = Math.max(0, parseInt(val || '0', 10) || 0);
    await onPatch({ metrics: { [editing]: n } });
    setEditing(null);
  };

  const addExp = async () => {
    if (!newExp.trim()) return;
    await onPatch({ experiments: [...exps, { title: newExp.trim(), status: 'running', result: '' }] });
    setNewExp('');
  };
  const setExpStatus = async (id, status) => onPatch({ experiments: exps.map((e) => (e.id === id ? { ...e, status } : e)) });
  const removeExp = async (id) => onPatch({ experiments: exps.filter((e) => e.id !== id) });

  const EXP_TONE = { running: 'border-blue-500/40 bg-blue-500/10 text-blue-700', completed: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700', killed: 'border-rose-500/40 bg-rose-500/10 text-rose-700' };

  return (
    <div className="space-y-8" data-testid="venture-growth-tab">
      <div>
        <p className="font-grotesk text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Traction — click a number to update it</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {METRICS_META.map((mm) => (
            <div key={mm.key} className="rounded-xl border border-border bg-card p-4" data-testid={`growth-metric-${mm.key}`}>
              {editing === mm.key ? (
                <div className="flex items-center gap-1.5">
                  <Input type="number" min="0" autoFocus value={val} onChange={(e) => setVal(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && saveMetric()} className="h-9" data-testid={`growth-metric-input-${mm.key}`} />
                  <Button size="icon" className="h-9 w-9 shrink-0" onClick={saveMetric} data-testid={`growth-metric-save-${mm.key}`}><Check className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="h-9 w-9 shrink-0" onClick={() => setEditing(null)}><X className="h-4 w-4" /></Button>
                </div>
              ) : (
                <button type="button" className="text-left w-full group" onClick={() => { setEditing(mm.key); setVal(String(m[mm.key] ?? 0)); }}
                  data-testid={`growth-metric-edit-${mm.key}`}>
                  <div className="font-display text-3xl flex items-center gap-2">{(m[mm.key] ?? 0).toLocaleString()}
                    <Pencil className="h-3 w-3 text-muted-foreground/0 group-hover:text-muted-foreground transition-colors" />
                  </div>
                </button>
              )}
              <div className="text-[10px] font-grotesk uppercase tracking-[0.16em] text-muted-foreground mt-1">{mm.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <p className="font-grotesk text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-3 flex items-center gap-2"><FlaskConical className="h-3.5 w-3.5" /> Growth experiments</p>
        <div className="flex gap-2 mb-3">
          <Input value={newExp} onChange={(e) => setNewExp(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addExp()}
            placeholder="e.g. Cold DM 50 school principals on LinkedIn" data-testid="experiment-new-input" />
          <Button onClick={addExp} className="rounded-full gap-1.5 shrink-0" data-testid="experiment-add-btn"><Plus className="h-4 w-4" /> Run it</Button>
        </div>
        {exps.length ? (
          <div className="space-y-2">
            {exps.map((e) => (
              <div key={e.id} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3" data-testid={`experiment-row-${e.id}`}>
                <Badge variant="outline" className={`text-[10px] uppercase shrink-0 ${EXP_TONE[e.status] || ''}`}>{e.status}</Badge>
                <p className="flex-1 min-w-0 text-sm truncate">{e.title}</p>
                <Select value={e.status} onValueChange={(v) => setExpStatus(e.id, v)}>
                  <SelectTrigger className="w-32 h-8 text-xs" data-testid={`experiment-status-${e.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="running">Running</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="killed">Killed</SelectItem>
                  </SelectContent>
                </Select>
                <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => removeExp(e.id)} data-testid={`experiment-remove-${e.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            ))}
          </div>
        ) : (
          <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            Ship experiments like a growth team — add one above and mark it completed or killed.
          </div>
        )}
      </div>
    </div>
  );
};

/* ───────────────────────── Team ───────────────────────── */

const TEAM_GROUPS = [
  { kind: 'founder', label: 'Founders' },
  { kind: 'cofounder', label: 'Co-founders' },
  { kind: 'mentor', label: 'Mentors' },
  { kind: 'intern', label: 'Interns' },
  { kind: 'advisor', label: 'Advisors' },
  { kind: 'investor', label: 'Investors' },
];

export const TeamTab = ({ venture, refresh }) => {
  const api = useApi();
  const team = venture.team || [];
  const invites = (venture.invites || []).filter((i) => i.status === 'pending');

  const addMember = async (payload) => {
    try {
      await api.post('/api/venture/me/team', payload);
      toast.success(`${payload.name || payload.email} added`);
      refresh();
    } catch (e) { toast.error('Could not add', { description: e.message }); throw e; }
  };
  const removeMember = async (id) => { await api.del(`/api/venture/me/team/${id}`); refresh(); };
  const invite = async (payload) => {
    if (!payload.email) return;
    try {
      await api.post('/api/venture/me/invites', payload);
      toast.success('Invite sent', { description: `${payload.email} — they'll be pinged in-app once they sign up.` });
      refresh();
    } catch (e) { toast.error('Could not invite', { description: e.message }); throw e; }
  };
  const cancelInvite = async (id) => { await api.del(`/api/venture/me/invites/${id}`); refresh(); };

  return (
    <div className="space-y-8" data-testid="venture-team-tab">
      <div className="flex items-center justify-between">
        <p className="font-grotesk text-[11px] uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-2"><Users className="h-3.5 w-3.5" /> Team · {team.length}</p>
      </div>

      <TeamMemberFinder
        invites={invites}
        onAdd={addMember}
        onInvite={invite}
        onCancelInvite={cancelInvite}
        testidPrefix="team"
      />

      {TEAM_GROUPS.map((g) => {
        const members = team.filter((t) => t.kind === g.kind);
        if (!members.length) return null;
        return (
          <div key={g.kind}>
            <p className="font-grotesk text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-2">{g.label}</p>
            <div className="grid sm:grid-cols-2 gap-2">
              {members.map((t) => (
                <div key={t.id} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3" data-testid={`team-member-${t.id}`}>
                  <Avatar className="h-9 w-9"><AvatarImage src={t.avatar} /><AvatarFallback>{(t.name || '?')[0]}</AvatarFallback></Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{t.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{t.role || g.label.replace(/s$/, '')}</p>
                  </div>
                  {g.kind !== 'founder' && (
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => removeMember(t.id)} data-testid={`team-remove-${t.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ───────────────────────── Timeline ───────────────────────── */

const ACT_ICON = { created: Rocket, stage: TrendingUp, metrics: TrendingUp, deck: Presentation, links: Link2, product: Smartphone, team: Users, achievement: Trophy, visibility: Eye };

export const TimelineTab = () => {
  const api = useApi();
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get('/api/venture/me/activity').then((d) => setItems(d.items || [])).catch(() => setItems([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!items) return <div className="text-sm text-muted-foreground py-6">Loading timeline…</div>;
  if (!items.length) {
    return (
      <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="timeline-empty">
        Your build-in-public log starts now — every deck update, metric change and milestone lands here.
      </div>
    );
  }
  return (
    <div className="relative pl-6" data-testid="venture-timeline-tab">
      <div className="absolute left-[9px] top-2 bottom-2 w-px bg-border" />
      <div className="space-y-5">
        {items.map((a) => {
          const Icon = ACT_ICON[a.kind] || FileText;
          return (
            <div key={a.id} className="relative" data-testid={`timeline-item-${a.id}`}>
              <div className="absolute -left-6 top-0.5 h-5 w-5 rounded-full bg-background border border-border grid place-items-center">
                <Icon className="h-2.5 w-2.5 text-muted-foreground" />
              </div>
              <p className="text-sm">{a.label}</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">{a.actor?.name ? `${a.actor.name} · ` : ''}{timeAgo(a.created_at)}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ───────────────────────── Resources ───────────────────────── */

export const ResourcesTab = ({ venture, openSettings }) => {
  const links = venture.links || {};
  const filled = LINKS_META.filter((l) => links[l.key]);
  const empty = LINKS_META.filter((l) => !links[l.key]);

  const copy = (v) => { navigator.clipboard?.writeText(v); toast.success('Copied'); };

  return (
    <div className="space-y-6" data-testid="venture-resources-tab">
      {filled.length ? (
        <div className="rounded-xl border border-border divide-y divide-border bg-card">
          {filled.map((l) => (
            <div key={l.key} className="flex items-center gap-3 p-3.5" data-testid={`resource-row-${l.key}`}>
              <div className="h-9 w-9 rounded-lg bg-secondary grid place-items-center shrink-0"><l.icon className="h-4 w-4" /></div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{l.label}</p>
                <p className="text-[11px] text-muted-foreground truncate">{links[l.key]}</p>
              </div>
              <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => copy(links[l.key])} data-testid={`resource-copy-${l.key}`}><Copy className="h-3.5 w-3.5" /></Button>
              <Button size="icon" variant="ghost" className="h-8 w-8" asChild><a href={links[l.key]} target="_blank" rel="noreferrer" data-testid={`resource-open-${l.key}`}><ExternalLink className="h-3.5 w-3.5" /></a></Button>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          One home for every link that matters — deck, demo, GitHub, socials.
        </div>
      )}
      {empty.length > 0 && (
        <div>
          <p className="font-grotesk text-[11px] uppercase tracking-[0.2em] text-muted-foreground mb-2">Not added yet</p>
          <div className="flex flex-wrap gap-2">
            {empty.map((l) => (
              <button key={l.key} type="button" onClick={() => openSettings('links')}
                className="flex items-center gap-1.5 rounded-full border border-dashed border-border px-3 py-1.5 text-xs text-muted-foreground hover:border-foreground/30 hover:text-foreground transition-colors"
                data-testid={`resource-add-${l.key}`}>
                <l.icon className="h-3 w-3" /> {l.label} <Plus className="h-3 w-3" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
